import { useRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from './cn.js';

interface OtpInputProps {
  value:    string;
  onChange: (v: string) => void;
  disabled?: boolean;
}

export function OtpInput({ value, onChange, disabled }: OtpInputProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  function inputs(): HTMLInputElement[] {
    return Array.from(containerRef.current?.querySelectorAll<HTMLInputElement>('input') ?? []);
  }

  function focus(idx: number) {
    inputs()[idx]?.focus();
  }

  function handleChange(idx: number, raw: string) {
    const digit = raw.replace(/\D/g, '').slice(-1);
    const chars = value.padEnd(6, ' ').split('');
    chars[idx]  = digit || ' ';
    onChange(chars.join('').trimEnd().slice(0, 6));
    if (digit && idx < 5) focus(idx + 1);
  }

  function handleKey(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Backspace') return;
    e.preventDefault();
    const chars = value.padEnd(6, ' ').split('');
    if (chars[idx]?.trim()) {
      chars[idx] = ' ';
      onChange(chars.join('').trimEnd());
    } else if (idx > 0) {
      focus(idx - 1);
      chars[idx - 1] = ' ';
      onChange(chars.join('').trimEnd());
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted);
    focus(Math.min(pasted.length, 5));
  }

  return (
    <div ref={containerRef} className="flex gap-2 justify-center" onPaste={handlePaste}>
      {Array.from({ length: 6 }).map((_, i) => {
        const digit = value[i] ?? '';
        return (
          <motion.input
            key={i}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKey(i, e)}
            disabled={disabled}
            aria-label={`Digit ${i + 1} of 6`}
            whileFocus={{ scale: 1.06 }}
            className={cn(
              'w-11 h-12 text-center text-lg font-bold border-2 rounded-xl transition-colors',
              'focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20',
              digit.trim()
                ? 'border-violet-400 bg-violet-50 text-violet-700'
                : 'border-gray-200 bg-white text-gray-900',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          />
        );
      })}
    </div>
  );
}
